"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

type Seg = { t: string; cut?: number; term?: boolean };

// `cut` is the scroll progress at which that stretch is struck out.
const SEGS: Seg[] = [
  {
    t: "It has long been observed, though not without some contestation across the relevant literatures, that ",
    cut: 0.16,
  },
  { t: "the demand curve, " },
  { t: "in most of its conventional specifications, ", cut: 0.24 },
  { t: "tends, " },
  { t: "ceteris paribus", term: true },
  { t: ", to slope downward, " },
  {
    t: "a tendency which, it has been variously argued, may be attributable in part to ",
    cut: 0.32,
  },
  { t: "the " },
  { t: "income effect", term: true },
  { t: " and in part to the " },
  { t: "substitution effect", term: true },
  {
    t: ", though the precise decomposition thereof has remained, and in many quarters continues to remain, a matter of no small methodological dispute.",
    cut: 0.4,
  },
];

const words = (s: string) => s.trim().split(/\s+/).length;
const TOTAL = SEGS.reduce((n, s) => n + words(s.t), 0);

// The scroll progress at which each stage begins. The chips jump to these.
const STAGES = [
  { name: "Assigned", at: 0 },
  { name: "Cut", at: 0.12 },
  { name: "Close up", at: 0.5 },
  { name: "Define", at: 0.62 },
  { name: "Rebuilt", at: 0.8 },
];

// How long the whole demo takes when it plays itself.
const PLAY_MS = 16000;

const clamp = (n: number) => Math.min(1, Math.max(0, n));

/**
 * A tall track with a pinned stage: the scroll position is the playhead.
 * Every stage is also a button, so nobody has to guess how to drive it, and
 * the same chips work from the keyboard. The student's reading is the paper
 * sheet; the dark around it is the app.
 */
export default function PinnedRebuild() {
  const track = useRef<HTMLElement>(null);
  const [p, setP] = useState(0);
  const [playing, setPlaying] = useState(false);
  const frame = useRef(0);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = track.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      setP(Math.round(clamp(span > 0 ? -r.top / span : 1) * 200) / 200);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  // Scrolls the page itself to where that stage lives on the track; the
  // stage then follows from the scroll position like any other scroll.
  const jump = (at: number) => {
    const el = track.current;
    if (!el) return;
    const span = el.offsetHeight - window.innerHeight;
    window.scrollTo({ top: el.offsetTop + span * at + 2, behavior: "smooth" });
  };

  const stop = () => {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    setPlaying(false);
  };

  // Plays the demo by scrolling the page for the visitor at a steady pace,
  // so the stages, the progress bar and the chips all stay in step with the
  // one thing that already drives them. Any wheel, touch or key press hands
  // control straight back.
  const play = () => {
    const el = track.current;
    if (!el) return;
    const span = el.offsetHeight - window.innerHeight;
    const from = p >= 0.97 ? 0 : p;
    const started = performance.now();
    const duration = (1 - from) * PLAY_MS;
    setPlaying(true);
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / duration);
      // `instant`, not the page's smooth default: a smooth scroll restarted
      // sixty times a second lags behind itself and never arrives.
      window.scrollTo({
        top: el.offsetTop + span * (from + (1 - from) * t) + 2,
        behavior: "instant",
      });
      if (t < 1) frame.current = requestAnimationFrame(tick);
      else stop();
    };
    frame.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (!playing) return;
    const handBack = () => {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
      setPlaying(false);
    };
    const events = ["wheel", "touchstart", "keydown"] as const;
    events.forEach((e) =>
      window.addEventListener(e, handBack, { passive: true }),
    );
    return () => events.forEach((e) => window.removeEventListener(e, handBack));
  }, [playing]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const stage = STAGES.reduce((acc, s, i) => (p >= s.at ? i : acc), 0);
  const closed = p >= 0.5;
  const rebuilt = p >= 0.8;
  const struckWords = SEGS.reduce(
    (n, s) => (s.cut !== undefined && p >= s.cut ? n + words(s.t) : n),
    0,
  );
  const scan = clamp((p - 0.1) / 0.34);

  return (
    <section
      id="example"
      ref={track}
      className="relative h-[400svh] bg-ink-alt"
      aria-label="Watch a reading rebuild"
    >
      <div className="sticky top-0 flex h-svh flex-col justify-center gap-5 px-5 pb-6 pt-24 sm:gap-6 sm:px-8 sm:pt-28">
        <div className="mx-auto w-full max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={playing ? stop : play}
                aria-pressed={playing}
                className="inline-flex items-center gap-2 bg-blue px-5 py-2 font-body text-base font-semibold text-blue-deep hover:-translate-y-0.5 hover:opacity-90"
              >
                <span aria-hidden>{playing ? "❚❚" : "▶"}</span>
                {playing
                  ? "Pause"
                  : p >= 0.97
                    ? "Play again"
                    : p > 0.02
                      ? "Keep playing"
                      : "Play it for me"}
              </button>
              <div
                role="group"
                aria-label="Stages"
                className="flex flex-wrap gap-2"
              >
                {STAGES.map((s, i) => (
                  <button
                    key={s.name}
                    onClick={() => jump(s.at)}
                    aria-current={stage === i ? "step" : undefined}
                    className={`px-4 py-2 font-body text-base sm:px-5 ${
                      stage === i
                        ? "bg-paper font-semibold text-ink"
                        : "bg-ink text-paper hover:bg-paper/15"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <p
              className="font-body text-base tabular-nums text-muted-dark"
              aria-live="polite"
            >
              {TOTAL - (closed ? struckWords : 0)} words{" "}
              <span aria-hidden>·</span> {rebuilt ? "2" : "6"} min read
            </p>
          </div>
        </div>

        {/* Both layers share one grid cell, so the sheet is as tall as the
            taller of the two and never clips its own text. */}
        <div className="mx-auto grid w-full max-w-6xl overflow-hidden rounded-[2rem] bg-paper text-ink">
          <div
            className="col-start-1 row-start-1 flex items-center p-6 sm:p-12"
            style={{
              opacity: rebuilt ? 0 : 1,
              transform: rebuilt ? "translateY(-12px)" : "none",
              transition: "opacity 300ms ease, transform 300ms ease",
              pointerEvents: rebuilt ? "none" : "auto",
            }}
            aria-hidden={rebuilt}
          >
            <div className="relative w-full max-w-4xl">
              <p className="font-reading text-[clamp(16px,1.9vw,26px)] leading-[1.6]">
                {SEGS.map((s, i) => {
                  const struck = s.cut !== undefined && p >= s.cut;
                  const gone = struck && closed;
                  const term = s.term && p >= 0.55;
                  return (
                    <span
                      key={i}
                      style={{
                        fontSize: gone ? 0 : undefined,
                        transition:
                          "font-size 700ms cubic-bezier(0.22,1,0.36,1), opacity 500ms, color 400ms, background-color 400ms",
                      }}
                      className={`${struck ? "text-strike line-through decoration-2" : ""} ${gone ? "opacity-0" : ""} ${
                        term ? "rounded bg-blue px-1" : ""
                      }`}
                    >
                      {s.t}
                    </span>
                  );
                })}
              </p>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-[-1.5%] h-0.5 rounded-full bg-ink shadow-[0_0_16px_3px_hsl(var(--blue)/0.8)] transition-opacity duration-300"
                style={{
                  top: `${scan * 100}%`,
                  opacity: p > 0.1 && p < 0.46 ? 1 : 0,
                }}
              />
              <dl
                className={`mt-7 grid gap-3 font-body text-base leading-relaxed text-muted-paper transition-[opacity,transform] duration-700 sm:grid-cols-3 ${
                  p >= 0.62
                    ? "translate-y-0 opacity-100"
                    : "translate-y-3 opacity-0"
                }`}
              >
                {[
                  ["ceteris paribus", "all else held equal"],
                  ["substitution effect", "buyers switch to alternatives"],
                  ["income effect", "the same budget buys less"],
                ].map(([term, def]) => (
                  <div key={term}>
                    <dt className="font-semibold text-ink">{term}</dt>
                    <dd>{def}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* The payoff. It waits for the old text to leave before it
              arrives, so the two never overlap. */}
          <div
            className="col-start-1 row-start-1 flex items-center p-6 sm:p-12"
            style={{
              opacity: rebuilt ? 1 : 0,
              transform: rebuilt ? "none" : "translateY(12px)",
              transition: `opacity 500ms ease ${rebuilt ? "300ms" : "0ms"}, transform 500ms ease ${rebuilt ? "300ms" : "0ms"}`,
              pointerEvents: rebuilt ? "auto" : "none",
            }}
            aria-hidden={!rebuilt}
          >
            <div className="max-w-3xl">
              <h3 className="font-heading text-[clamp(26px,3.4vw,46px)] leading-[1.15]">
                Demand curves slope down for{" "}
                <span className="rounded bg-blue px-2 [box-decoration-break:clone]">
                  two separate reasons.
                </span>
              </h3>
              <p className="mt-5 font-reading text-[clamp(16px,1.7vw,23px)] leading-relaxed">
                The{" "}
                <mark className="rounded bg-blue px-1 text-ink">
                  substitution effect
                </mark>
                : the good is pricier relative to alternatives, so buyers
                switch. The{" "}
                <mark className="rounded bg-blue px-1 text-ink">
                  income effect
                </mark>
                : the same budget now buys less of everything.{" "}
                <span className="text-muted-paper">You need both.</span>
              </p>
              <Link
                href="/reading/new"
                tabIndex={rebuilt ? 0 : -1}
                className="group mt-8 inline-flex items-center bg-ink px-7 py-3.5 font-body text-base font-semibold text-paper hover:-translate-y-0.5 hover:bg-ink-alt"
              >
                Rebuild your own reading{" "}
                <span
                  aria-hidden
                  className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-6xl items-center gap-5">
          <div
            className="h-2 flex-1 overflow-hidden rounded-full bg-paper/10"
            aria-hidden
          >
            <div
              className="h-full origin-left rounded-full bg-blue"
              style={{ transform: `scaleX(${p})` }}
            />
          </div>
          <p
            className={`hidden font-body text-base text-muted-dark transition-opacity duration-500 sm:block ${p < 0.03 ? "opacity-100" : "opacity-0"}`}
          >
            Scroll to watch it work{" "}
            <span aria-hidden className="inline-block animate-bounce">
              ↓
            </span>
          </p>
          <a
            href="#how"
            className="font-body text-base text-blue underline underline-offset-4 hover:opacity-80"
          >
            Skip the demo
          </a>
        </div>
      </div>
    </section>
  );
}
